import api from "@/api/api.js";
import { DataLoadState } from "@/utils/storeUtils.js";
import { eventManager, HUNT_MEMBERS_CHANGED_EVENT } from "@/eventManager.js";


// Mutation names
const SET_HUNT_ID = "setHuntId";
const SET_HUNT_LOAD_STARTED = "setHuntLoadStarted";
const SET_HUNT_LOAD_FINISHED_SUCCESSFULLY = "setHuntLoadFinishedSuccessfully";
const SET_HUNT_LOAD_FAILED = "setHuntLoadFailed";

const SET_MEMBERS_LOAD_STARTED = "setMembersLoadStarted";
const SET_MEMBERS_LOAD_FINISHED_SUCCESSFULLY = "setMembersLoadFinishedSuccessfully";
const SET_MEMBERS_LOAD_FAILED = "setMembersLoadFailed";

const RESET_MEMBERS = "resetMembers";

// Getter names
export const IS_LOADING_HUNT = "isLoadingHunt";
export const GET_HUNT = "getHunt";
export const GET_HUNT_LOAD_ERROR = "getHuntLoadError";

export const IS_LOADING_MEMBERS = "isLoadingMembers";
export const GET_MEMBERS = "getMembers";
export const GET_MEMBERS_LOAD_ERROR = "getMembersLoadError";
export const HAS_EMPTY_MEMBERS_LOAD_RESULT = "hasEmptyMembersLoadResult";

// Action names
export const LOAD_HUNT_WITH_ID = "loadHuntWithId";
export const SET_HUNT = "setHunt";
export const LOAD_MEMBERS = "loadMembers";
export const REMOVE_MEMBER_WITH_ID = "removeMemberWithId";


// Util functions
function sortMemberByName(member, secondMember) {
  const name = `${member.firstName} ${member.lastName}`
  const secondMemberName = `${secondMember.firstName} ${secondMember.lastName}`
  return name.localeCompare(secondMemberName);
}


// Hunt store implementation
export const HuntStore = {
  namespaced: true,

  state: () => ({ // state must be a function to allow reuse of this store
    huntId: null,
    huntState: new DataLoadState(),
    membersState: new DataLoadState()
  }),

  mutations: {
    [SET_HUNT_ID](state, { huntId }) {
      state.huntId = huntId;
    },

    [SET_HUNT](state, { hunt }) {
      if (!hunt || !hunt.id) {
        state.huntAreaState = new DataLoadState();
        state.membersState = new DataLoadState();
        return;
      }

      state.huntState = new DataLoadState(hunt.id).stateForLoadFinished(hunt);
    },

    [SET_HUNT_LOAD_STARTED](state, { huntId }) {
      const dataLoadState = new DataLoadState(huntId);
      state.huntState = dataLoadState.stateForLoadStarted();
    },

    [SET_HUNT_LOAD_FINISHED_SUCCESSFULLY](state, { hunt }) {
      if (hunt.id !== state.huntState.id) {
        // Hunt load is stale, ignore
        return;
      }

      state.huntState = state.huntState.stateForLoadFinished(hunt);
    },

    [SET_HUNT_LOAD_FAILED](state, { huntId, error }) {
      if (huntId !== state.huntState.id) {
        // Hunt load is stale, ignore
        return;
      }

      state.huntState = state.huntState.stateForLoadFailed(error);
    },

    [SET_MEMBERS_LOAD_STARTED](state, { huntId }) {
      const dataLoadState = new DataLoadState(huntId);
      state.membersState = dataLoadState.stateForLoadStarted();
    },

    [SET_MEMBERS_LOAD_FINISHED_SUCCESSFULLY](state, { huntId, members }) {
      if (huntId !== state.membersState.id) {
        // Members load is stale, ignore
        return;
      }

      state.membersState = state.membersState.stateForLoadFinished(members);
    },

    [SET_MEMBERS_LOAD_FAILED](state, { huntId, error }) {
      if (huntId !== state.membersState.id) {
        // Members load is stale, ignore
        return;
      }

      state.membersState = state.membersState.stateForLoadFailed(error);
    },

    [RESET_MEMBERS](state) {
      state.membersState = new DataLoadState();
    }
  },

  getters: {
    [IS_LOADING_HUNT]: state => {
      return state.huntState.status.loading;
    },

    [GET_HUNT]: state => {
      return state.huntState.data;
    },

    [GET_HUNT_LOAD_ERROR]: state => {
      return state.huntState.status.error;
    },

    [IS_LOADING_MEMBERS]: state => {
      return state.membersState.status.loading;
    },

    [GET_MEMBERS]: state => {
      return state.membersState.data;
    },

    [GET_MEMBERS_LOAD_ERROR]: state => {
      return state.membersState.status.error;
    },

    [HAS_EMPTY_MEMBERS_LOAD_RESULT]: state => {
      return state.membersState.status.success && state.membersState.data.length === 0;
    }
  },

  actions: {
    [LOAD_HUNT_WITH_ID]: async ({ commit, state }, { huntId }) => {
      if (!huntId) {
        return;
      }

      if (huntId === state.huntState.id) {
        // Hunt load has already started
        return;
      }

      commit(SET_HUNT_ID, { huntId });
      commit(SET_HUNT_LOAD_STARTED, { huntId });

      try {
        const response = await api.get(`admin/hunts/${huntId}`);
        const hunt = response.data;

        commit(SET_HUNT_LOAD_FINISHED_SUCCESSFULLY, { hunt });
      } catch (error) {
        commit(SET_HUNT_LOAD_FAILED, { huntId, error });
      }
    },

    [SET_HUNT]: ({ commit }, { hunt }) => {
      var huntId = null;
      if (hunt) {
        huntId = hunt.id || null;
      }

      commit(SET_HUNT_ID, { huntId });
      commit(SET_HUNT, { hunt });
    },

    [LOAD_MEMBERS]: async ({ commit, state }) => {
      const huntId = state.huntId;
      if (!huntId) {
        return;
      }

      if (huntId === state.membersState.id) {
        // Members load has already started
        return;
      }

      commit(SET_MEMBERS_LOAD_STARTED, { huntId });

      try {
        const response = await api.get(
          `admin/hunts/${huntId}/members`
        );

        const members = response.data.slice(0).sort(sortMemberByName);
        commit(SET_MEMBERS_LOAD_FINISHED_SUCCESSFULLY, { huntId, members });
      } catch (error) {
        commit(SET_MEMBERS_LOAD_FAILED, { huntId, error });
      }
    },

    [REMOVE_MEMBER_WITH_ID]: ({ commit, dispatch }, { huntId, memberId }) => {
      return api.delete(
        `admin/hunts/${huntId}/members/${memberId}`
      ).then(() => {
        // Always reload members to make sure the list is correct.
        commit(RESET_MEMBERS);
        dispatch(LOAD_MEMBERS);

        // Emit event to allow other stores to reload or delete remove stale data
        eventManager.$emit(HUNT_MEMBERS_CHANGED_EVENT, { huntId, memberId });
      });
    }
  }
};
